import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import useAuth from '../../hooks/useAuth';
import logger from '../../utils/logger';

const ShareDialog = ({ open, onClose, item, collectionName, onShare }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentUser?.uid || !currentUser?.familyCode) {
        setError('User must be part of a family to share items');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Query users in the same family
        const familyUsersQuery = query(
          collection(db, 'users'),
          where('familyCode', '==', currentUser.familyCode)
        );

        const querySnapshot = await getDocs(familyUsersQuery);
        const usersList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(user => user.uid !== currentUser.uid); // Filter out current user after fetching

        setUsers(usersList);
        
        // If the item is already shared, pre-select those users
        if (item?.sharedWith) {
          setSelectedUsers(item.sharedWith);
        }
      } catch (err) {
        logger.error('Error fetching users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (open && item) {
      fetchUsers();
    } else {
      setSelectedUsers([]);
    }
  }, [open, item, currentUser]);

  const handleToggleUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (!item) {
      setError('No item selected for sharing');
      return;
    }

    try {
      await onShare(item, selectedUsers);
      onClose();
    } catch (err) {
      logger.error('Error sharing item:', err);
      setError('Failed to share the item. Please try again.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      aria-labelledby="share-dialog-title"
      aria-describedby="share-dialog-description"
    >
      <DialogTitle id="share-dialog-title">
        Share with Family Members
      </DialogTitle>
      <DialogContent id="share-dialog-description">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress data-testid="CircularProgress" />
          </div>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : users.length === 0 ? (
          <Typography>No family members available to share with</Typography>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select family members to share this {item?.type || 'item'} with:
            </Typography>
            <List>
              {users.map((user) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => handleToggleUser(user.id)}
                  role="checkbox"
                  aria-checked={selectedUsers.includes(user.id)}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedUsers.includes(user.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText 
                    primary={user.displayName || user.email} 
                    secondary={user.email}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleShare}
          variant="contained"
          disabled={loading || selectedUsers.length === 0}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
